// src/pages/OdooConfigurations.tsx
import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout.tsx';
import { odooConfigAPI, OdooConfiguration } from '../services/api.ts';
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import {
  ChartColumn,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ServerCog,
} from 'lucide-react';
import {
  ConfirmModal,
  EmptyState,
  PageHeader,
} from '../components/app/index.ts';
import { toast } from '../lib/toast.ts';
import { OdooConfigurationForm } from '../components/odoo/OdooConfigurationForm.tsx';
import { useSync } from '../contexts/SyncContext.tsx';
import { OdooConfigurationEditForm } from '../components/odoo/OdooConfigurationEditForm.tsx';

export const OdooConfigurations: React.FC = () => {
  const [configurations, setConfigurations] = useState<OdooConfiguration[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editConfig, setEditConfig] = useState<OdooConfiguration | null>(null);
  // Same call shape as the `toast` state this page used to keep.
  const setToast = ({
    message,
    type,
  }: {
    message: string;
    type: 'success' | 'error';
  }) => (type === 'success' ? toast.success(message) : toast.error(message));
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    configId: number | null;
  }>({
    isOpen: false,
    configId: null,
  });

  const { isSyncing, startSync, updateSync, endSync } = useSync();

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const response = await odooConfigAPI.getAll();
      if (response.success && response.data) {
        setConfigurations(response.data.configurations);
      }
    } catch (error: any) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await odooConfigAPI.delete(id);
      setToast({
        message: 'Configuration deleted successfully',
        type: 'success',
      });
      await fetchConfigurations();
    } catch (error: any) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
      setDeleteConfirmation({ isOpen: false, configId: null });
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = await checkOddooConnection(values);
      if (!data) return;
      setLoading(true);
      await odooConfigAPI.create(values);
      setToast({
        message: 'Configuration created successfully',
        type: 'success',
      });
      setIsModalOpen(false);
      await fetchConfigurations();
    } catch (error: any) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorize = (configId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Get the stream URL - this will use Lambda Function URL if STREAM_API_URL is set
      let url = odooConfigAPI.getAuthorizationStream(configId);

      // Add token as query parameter
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}token=${token}`;

      // Begin syncing with an initial message
      startSync('Connecting to Odoo...');

      // Create the EventSource to listen to server-sent events
      const eventSource = new EventSource(url);

      // When a message is received, update the sync message
      eventSource.onmessage = (event) => {
        const data = event.data;

        // Check if this is the completion message
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'complete') {
            setToast({
              message: parsed.success
                ? 'Configuration synced successfully'
                : `Sync completed with ${parsed.errors} errors`,
              type: parsed.success ? 'success' : 'error',
            });
            eventSource.close();
            endSync();
            fetchConfigurations(); // Refresh the list
            return;
          }
        } catch {
          // Not JSON, just a status message
        }

        updateSync(data);
      };

      // Handle errors from the event stream
      eventSource.onerror = (error) => {
        console.error('Authorization error:', error);
        setToast({
          message: 'Failed to connect to sync stream',
          type: 'error',
        });
        eventSource.close();
        endSync();
      };
    } catch (error: any) {
      console.error('Authorization error:', error);
      setToast({ message: error.message, type: 'error' });
      endSync();
    }
  };

  const handleEdit = async (values: any) => {
    if (!editConfig) return;
    try {
      const data = await checkOddooConnection(values);
      if (!data) return;
      setLoading(true);
      await odooConfigAPI.update(editConfig?.id, values);
      setToast({
        message: 'Configuration updated successfully',
        type: 'success',
      });
      setEditModalOpen(false);
      setEditConfig(null);
      await fetchConfigurations();
    } catch (error: any) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const checkOddooConnection = async (config: OdooConfiguration) => {
    const data = await odooConfigAPI.checkOdooConnectivity({
      url: config.url,
      db: config.db,
      username: config.username,
      password: config.password || '',
    });

    if (!data.success) {
      setToast({ message: data.message, type: 'error' });
      return false;
    }

    setToast({ message: data.message, type: 'success' });
    return true;
  };

  const filteredConfigurations = configurations.filter((config) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      config.name?.toLowerCase().includes(q) ||
      config.url?.toLowerCase().includes(q) ||
      config.db?.toLowerCase().includes(q) ||
      config.username?.toLowerCase().includes(q)
    );
  });

  const formatDate = (value: any) =>
    new Date(value).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });

  return (
    <Layout>
      <PageHeader
        title="Odoo Configurations"
        description="Manage your Odoo server configurations and connections."
        actions={
          <Button
            leftSection={<Plus size={16} />}
            onClick={() => setIsModalOpen(true)}
          >
            Add Configuration
          </Button>
        }
      />

      <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
        <Stack gap="md">
          <TextInput
            type="search"
            placeholder="Search by name, URL, DB, user..."
            leftSection={<Search size={16} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
          />

          {loading ? (
            <Stack align="center" justify="center" gap="xs" h={160}>
              <Loader />
              <Text size="sm" c="dimmed">
                Loading configurations...
              </Text>
            </Stack>
          ) : filteredConfigurations.length > 0 ? (
            <Table.ScrollContainer minWidth={980}>
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>URL</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Database</Table.Th>
                    <Table.Th>Username</Table.Th>
                    <Table.Th>Created At</Table.Th>
                    <Table.Th>Updated At</Table.Th>
                    <Table.Th ta="right">Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredConfigurations.map((config) => (
                    <Table.Tr key={config.id}>
                      <Table.Td maw={320} style={{ wordBreak: 'break-word' }}>
                        {config.url}
                      </Table.Td>
                      <Table.Td>{config.name}</Table.Td>
                      <Table.Td>{config.db}</Table.Td>
                      <Table.Td>{config.username}</Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {formatDate(config.created_at)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {formatDate(config.updated_at)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end" wrap="nowrap">
                          <ActionIcon
                            size="lg"
                            aria-label="Sync data from Odoo"
                            onClick={() => handleAuthorize(config.id)}
                            disabled={isSyncing}
                            loading={isSyncing}
                          >
                            <RefreshCw size={18} />
                          </ActionIcon>
                          <ActionIcon
                            size="lg"
                            variant="light"
                            aria-label="Check Odoo connection"
                            onClick={() => {
                              checkOddooConnection(config);
                            }}
                          >
                            <ChartColumn size={18} />
                          </ActionIcon>
                          <ActionIcon
                            size="lg"
                            variant="default"
                            aria-label="Edit configuration"
                            onClick={() => {
                              setEditConfig(config);
                              setEditModalOpen(true);
                            }}
                          >
                            <Pencil size={18} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <EmptyState
              icon={<ServerCog size={28} />}
              title="No configurations"
              description={
                searchTerm
                  ? 'Try a different search term.'
                  : 'Get started by adding a new Odoo configuration.'
              }
            />
          )}
        </Stack>
      </Paper>

      {/* Odoo Configuration Modal */}
      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        withCloseButton={false}
        size="xl"
      >
        <OdooConfigurationForm
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <Modal
        opened={!!editConfig && editModalOpen}
        onClose={() => setEditModalOpen(false)}
        withCloseButton={false}
        size="xl"
      >
        {editConfig && (
          <OdooConfigurationEditForm
            onSubmit={handleEdit}
            onCancel={() => setEditModalOpen(false)}
            initialValues={editConfig}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        opened={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, configId: null })}
        onConfirm={() => {
          if (deleteConfirmation.configId) {
            handleDelete(deleteConfirmation.configId);
          }
        }}
        title="Delete Configuration"
        confirmLabel="Delete"
      >
        Are you sure you want to delete this Odoo configuration? This action
        cannot be undone.
      </ConfirmModal>
    </Layout>
  );
};
